import { Dtos } from "@inrev/common";
import {
	useFetchSfaaClassExperienceFilters,
	useFetchSfaaClassExperienceSeries,
} from "@portal/api/sfaa-class-experience/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { Banner, Icon, Spinner, Text } from "@portal/components/primitives";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { MarketAnalyticsSection } from "./MarketAnalyticsSection";
import { SfaaLossRatioChart } from "./SfaaLossRatioChart";

const { US_TOTAL_LOCATION, codeSeriesOf } = Dtos.Admin.SfaaClassExperience;

/** Must be non-empty because the dropdown reserves `""`; the API suppresses same-entity overlays. */
const NO_COMPARISON = "NONE";
const ALL_CODES = "ALL_CODES";

type Selection = {
	codeSeries: string | undefined;
	location: string | undefined;
	comparison: string;
	sfaaCode: string | undefined;
};

const toOptions = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

const Field = ({
	label,
	width,
	children,
}: {
	label: string;
	width: string;
	children: ReactNode;
}) => (
	<div className="flex flex-col gap-1">
		<Text type="caption" color="tertiary">
			{label}
		</Text>
		<div className={width}>{children}</div>
	</div>
);

const comparisonForLocation = (location: string, currentComparison: string): string => {
	if (currentComparison !== NO_COMPARISON && currentComparison !== location) {
		return currentComparison;
	}
	return location === US_TOTAL_LOCATION ? NO_COMPARISON : US_TOTAL_LOCATION;
};

export const SfaaLossRatioTab = () => {
	const filters = useFetchSfaaClassExperienceFilters();
	const [selection, setSelection] = useState<Selection>({
		codeSeries: undefined,
		location: undefined,
		comparison: NO_COMPARISON,
		sfaaCode: undefined,
	});

	const seriesOptions = useMemo(() => {
		const prefixes = new Set((filters.data?.sfaaCodes ?? []).map(codeSeriesOf));
		return [...prefixes].sort().map((value) => ({ value, label: value }));
	}, [filters.data?.sfaaCodes]);

	const locationOptions = useMemo(() => {
		const states = [...(filters.data?.locations ?? [])]
			.filter((location) => location !== US_TOTAL_LOCATION)
			.sort();
		return [{ value: US_TOTAL_LOCATION, label: US_TOTAL_LOCATION }, ...toOptions(states)];
	}, [filters.data?.locations]);

	const codeSeries = selection.codeSeries ?? seriesOptions[0]?.value;
	const location = selection.location ?? US_TOTAL_LOCATION;
	const comparisonQuery = selection.comparison === NO_COMPARISON ? location : selection.comparison;

	const series = useFetchSfaaClassExperienceSeries({
		codeSeries,
		location,
		comparison: comparisonQuery,
		sfaaCode: selection.sfaaCode,
	});

	const codeOptions = useMemo(
		() => [
			{ value: ALL_CODES, label: "All codes" },
			...toOptions(series.data?.availableSfaaCodes ?? []),
		],
		[series.data?.availableSfaaCodes],
	);

	const comparisonOptions = useMemo(
		() => [{ value: NO_COMPARISON, label: "None" }, ...locationOptions],
		[locationOptions],
	);

	const onCodeSeriesChange = useCallback((value: string) => {
		setSelection((current) => ({ ...current, codeSeries: value, sfaaCode: undefined }));
	}, []);

	const onSfaaCodeChange = useCallback((value: string) => {
		setSelection((current) => ({
			...current,
			sfaaCode: value === ALL_CODES ? undefined : value,
		}));
	}, []);

	const onLocationChange = useCallback((value: string) => {
		setSelection((current) => ({
			...current,
			location: value,
			comparison: comparisonForLocation(value, current.comparison),
		}));
	}, []);

	const onComparisonChange = useCallback((value: string) => {
		setSelection((current) => ({ ...current, comparison: value }));
	}, []);

	if (filters.data === undefined && filters.error !== null) {
		return (
			<ReportedRetryableLoadError
				resource="loss ratio filters"
				error={filters.error}
				onRetry={filters.refetch}
				retrying={filters.isFetching}
			/>
		);
	}

	if (filters.data === undefined) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col gap-6 overflow-auto pb-4">
			<div className="flex shrink-0 flex-wrap items-end gap-x-4 gap-y-3">
				<Field label="Series" width="w-[120px]">
					<DropdownInput
						value={codeSeries ?? ""}
						options={seriesOptions}
						onChange={onCodeSeriesChange}
						testId="sfaa-series-select"
					/>
				</Field>
				<Field label="Code" width="w-[160px]">
					<DropdownInput
						value={selection.sfaaCode ?? ALL_CODES}
						options={codeOptions}
						onChange={onSfaaCodeChange}
						testId="sfaa-code-select"
					/>
				</Field>
				<Field label="Location" width="w-[200px]">
					<DropdownInput
						value={location}
						options={locationOptions}
						onChange={onLocationChange}
						testId="sfaa-location-select"
					/>
				</Field>
				<Field label="Compare to" width="w-[200px]">
					<DropdownInput
						value={selection.comparison}
						options={comparisonOptions}
						onChange={onComparisonChange}
						disabledOptions={[location]}
						testId="sfaa-comparison-select"
					/>
				</Field>
			</div>

			{series.error !== null && series.data !== undefined && (
				<Banner tone="danger">
					<Banner.Leading>
						<Icon type="exclamation-circle" size="small" color="danger" />
					</Banner.Leading>
					<Banner.Content>
						<Text type="body" color="danger">
							Showing the last loaded figures. Refreshing failed.
						</Text>
					</Banner.Content>
				</Banner>
			)}

			{series.data === undefined && series.error !== null ? (
				<ReportedRetryableLoadError
					resource="loss ratio series"
					error={series.error}
					onRetry={series.refetch}
					retrying={series.isFetching}
				/>
			) : series.data === undefined ? (
				<div className="flex flex-1 items-center justify-center">
					<Spinner />
				</div>
			) : (
				<MarketAnalyticsSection
					title="Loss ratio"
					subtitle="Incurred loss over earned premium"
					testId="sfaa-loss-ratio-section"
				>
					<div className="flex flex-col gap-5">
						<SfaaLossRatioChart
							title="Annual loss ratio"
							caption="Incurred over earned. A state code overlays the national series book, not the same code."
							years={series.data.years}
							selected={series.data.selected}
							comparison={series.data.comparison}
							metric="lossRatio"
							testId="sfaa-annual-loss-ratio-chart"
						/>
						<SfaaLossRatioChart
							title="Loss ratio, 3-year trailing"
							caption="Three-year sums, so a single thin year does not dominate. Starts in the third year."
							years={series.data.years}
							selected={series.data.selected}
							comparison={series.data.comparison}
							metric="trailingLossRatio"
							testId="sfaa-trailing-loss-ratio-chart"
						/>
					</div>
				</MarketAnalyticsSection>
			)}
		</div>
	);
};
